import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { DisposableStore, Disposable, MutableDisposable } from 'vscode/vscode/vs/base/common/lifecycle';
import { localize2WithPath } from 'vscode/vscode/vs/nls';
import { CONTEXT_ACCESSIBILITY_MODE_ENABLED } from 'vscode/vscode/vs/platform/accessibility/common/accessibility';
import { Action2, registerAction2 } from 'vscode/vscode/vs/platform/actions/common/actions';
import { ContextKeyExpr } from 'vscode/vscode/vs/platform/contextkey/common/contextkey';
import { IContextKeyService } from 'vscode/vscode/vs/platform/contextkey/common/contextkey.service';
import { IInstantiationService } from 'vscode/vscode/vs/platform/instantiation/common/instantiation';
import { accessibleViewCurrentProviderId, accessibleViewIsShown } from 'vscode/vscode/vs/workbench/contrib/accessibility/browser/accessibilityConfiguration';
import { IAccessibleViewService } from 'vscode/vscode/vs/workbench/contrib/accessibility/browser/accessibleView.service';
import { AccessibleViewAction, AccessibilityHelpAction } from 'vscode/vscode/vs/workbench/contrib/accessibility/browser/accessibleViewActions';
import { ITerminalService } from 'vscode/vscode/vs/workbench/contrib/terminal/browser/terminal.service';
import { registerTerminalAction } from '../../../terminal/browser/terminalActions.js';
import { registerTerminalContribution } from '../../../terminal/browser/terminalExtensions.js';
import { TerminalContextKeys } from 'vscode/vscode/vs/workbench/contrib/terminal/common/terminalContextKey';
import { BufferContentTracker } from './bufferContentTracker.js';
import { TerminalAccessibilityHelpProvider } from './terminalAccessibilityHelp.js';
import { TextAreaSyncAddon } from './textAreaSyncAddon.js';
import { Position } from 'vscode/vscode/vs/editor/common/core/position';
import { TerminalAccessibleBufferProvider } from './terminalAccessibleBufferProvider.js';
import { IConfigurationService } from 'vscode/vscode/vs/platform/configuration/common/configuration.service';
import { Event } from 'vscode/vscode/vs/base/common/event';
import { AccessibilitySignal } from 'vscode/vscode/vs/platform/accessibilitySignal/browser/accessibilitySignalService';
import { IAccessibilitySignalService } from 'vscode/vscode/vs/platform/accessibilitySignal/browser/accessibilitySignalService.service';
import { alert } from 'vscode/vscode/vs/base/browser/ui/aria/aria';

var TextAreaSyncContribution_1, TerminalAccessibleViewContribution_1;
const _moduleId = "vs/workbench/contrib/terminalContrib/accessibility/browser/terminal.accessibility.contribution";
let TextAreaSyncContribution = class TextAreaSyncContribution extends DisposableStore {
    static { TextAreaSyncContribution_1 = this; }
    static { this.ID = 'terminal.textAreaSync'; }
    static get(instance) {
        return instance.getContribution(TextAreaSyncContribution_1.ID);
    }
    constructor(_instance, processManager, widgetManager, _instantiationService) {
        super();
        this._instance = _instance;
        this._instantiationService = _instantiationService;
    }
    layout(xterm) {
        if (this._addon) {
            return;
        }
        this._addon = this.add(this._instantiationService.createInstance(TextAreaSyncAddon, this._instance.capabilities));
        xterm.raw.loadAddon(this._addon);
        this._addon.activate(xterm.raw);
    }
};
TextAreaSyncContribution = TextAreaSyncContribution_1 = ( (__decorate([
    ( (__param(3, IInstantiationService)))
], TextAreaSyncContribution)));
registerTerminalContribution(TextAreaSyncContribution.ID, TextAreaSyncContribution);
let TerminalAccessibleViewContribution = class TerminalAccessibleViewContribution extends Disposable {
    static { TerminalAccessibleViewContribution_1 = this; }
    static { this.ID = 'terminal.accessibleBufferProvider'; }
    static get(instance) {
        return instance.getContribution(TerminalAccessibleViewContribution_1.ID);
    }
    constructor(_instance, processManager, widgetManager, _accessibleViewService, _instantiationService, _terminalService, _configurationService, _contextKeyService, _accessibilitySignalService) {
        super();
        this._instance = _instance;
        this._accessibleViewService = _accessibleViewService;
        this._instantiationService = _instantiationService;
        this._terminalService = _terminalService;
        this._configurationService = _configurationService;
        this._contextKeyService = _contextKeyService;
        this._accessibilitySignalService = _accessibilitySignalService;
        this._onDidRunCommand = ( (new MutableDisposable()));
        this._register(AccessibleViewAction.addImplementation(90, 'terminal', () => {
            if (this._terminalService.activeInstance !== this._instance) {
                return false;
            }
            this.show();
            return true;
        }, TerminalContextKeys.focus));
        this._register(_instance.onDidExecuteText(() => {
            const focusAfterRun = _configurationService.getValue("terminal.integrated.focusAfterRun" );
            if (focusAfterRun === 'terminal') {
                _instance.focus(true);
            }
            else if (focusAfterRun === 'accessible-buffer') {
                this.show();
            }
        }));
        this._register(this._configurationService.onDidChangeConfiguration(e => {
            if (e.affectsConfiguration("terminal.integrated.accessibleViewFocusOnCommandExecution" )) {
                this._updateCommandExecutedListener();
            }
        }));
        this._register(this._instance.capabilities.onDidAddCapability(e => {
            if (e.capability.type === 2 ) {
                this._updateCommandExecutedListener();
            }
        }));
    }
    xtermReady(xterm) {
        const addon = this._instantiationService.createInstance(TextAreaSyncAddon, this._instance.capabilities);
        xterm.raw.loadAddon(addon);
        addon.activate(xterm.raw);
        this._xterm = xterm;
        this._register(this._xterm.raw.onWriteParsed(async () => {
            if (this._terminalService.activeInstance !== this._instance) {
                return;
            }
            if (this._isTerminalAccessibleViewOpen() && this._xterm.raw.buffer.active.baseY === 0) {
                this.show();
            }
        }));
        const onRequestUpdateEditor = Event.latch(this._xterm.raw.onScroll);
        this._register(onRequestUpdateEditor(() => {
            if (this._terminalService.activeInstance !== this._instance) {
                return;
            }
            if (this._isTerminalAccessibleViewOpen()) {
                this.show();
            }
        }));
    }
    _updateCommandExecutedListener() {
        if (!( (this._instance.capabilities.has(2 )))) {
            return;
        }
        if (!this._configurationService.getValue("terminal.integrated.accessibleViewFocusOnCommandExecution" )) {
            this._onDidRunCommand.clear();
            return;
        }
        else if (this._onDidRunCommand.value) {
            return;
        }
        const capability = this._instance.capabilities.get(2 );
        this._onDidRunCommand.value = this._register(capability.onCommandExecuted(() => {
            if (this._instance.hasFocus) {
                this.show();
            }
        }));
    }
    _isTerminalAccessibleViewOpen() {
        return accessibleViewCurrentProviderId.getValue(this._contextKeyService) === "terminal" ;
    }
    show() {
        if (!this._xterm) {
            return;
        }
        if (!this._bufferTracker) {
            this._bufferTracker = this._register(this._instantiationService.createInstance(BufferContentTracker, this._xterm));
        }
        if (!this._bufferProvider) {
            this._bufferProvider = this._register(this._instantiationService.createInstance(TerminalAccessibleBufferProvider, this._instance, this._bufferTracker, () => {
                return this._register(this._instantiationService.createInstance(TerminalAccessibilityHelpProvider, this._instance, this._xterm)).provideContent();
            }));
        }
        const position = this._configurationService.getValue("terminal.integrated.accessibleViewPreserveCursorPosition" ) ? this._accessibleViewService.getPosition("terminal" ) : undefined;
        this._accessibleViewService.show(this._bufferProvider, position);
    }
    navigateToCommand(type) {
        const currentLine = this._accessibleViewService.getPosition("terminal" )?.lineNumber;
        const commands = this._getCommandsWithEditorLine();
        if (!commands?.length || !currentLine) {
            return;
        }
        const filteredCommands = type === "previous"  ? commands.filter(c => c.lineNumber < currentLine).sort((a, b) => b.lineNumber - a.lineNumber) : commands.filter(c => c.lineNumber > currentLine).sort((a, b) => a.lineNumber - b.lineNumber);
        if (!filteredCommands.length) {
            return;
        }
        const command = filteredCommands[0];
        this._accessibleViewService.setPosition(( (new Position(command.lineNumber, 1))), true);
        const commandLine = command.command.command;
        if (commandLine) {
            alert(commandLine);
        }
        if (command.exitCode) {
            this._accessibilitySignalService.playSignal(AccessibilitySignal.terminalCommandFailed);
        }
    }
    _getCommandsWithEditorLine() {
        const capability = this._instance.capabilities.get(2 );
        const commands = capability?.commands;
        const currentCommand = capability?.currentCommand;
        if (!commands?.length) {
            return;
        }
        const result = [];
        for (const command of commands) {
            const lineNumber = this._getEditorLineForCommand(command);
            if (!lineNumber) {
                continue;
            }
            result.push({ command, lineNumber, exitCode: command.exitCode });
        }
        if (currentCommand) {
            const lineNumber = this._getEditorLineForCommand(currentCommand);
            if (!!lineNumber) {
                result.push({ command: currentCommand, lineNumber });
            }
        }
        return result;
    }
    _getEditorLineForCommand(command) {
        if (!this._bufferTracker) {
            return;
        }
        let line;
        if ('marker' in command) {
            line = command.marker?.line;
        }
        else if ('commandStartMarker' in command) {
            line = command.commandStartMarker?.line;
        }
        if (line === undefined || line < 0) {
            return;
        }
        line = this._bufferTracker.bufferToEditorLineMapping.get(line);
        if (line === undefined) {
            return;
        }
        return line + 1;
    }
};
TerminalAccessibleViewContribution = TerminalAccessibleViewContribution_1 = ( (__decorate([
    ( (__param(3, IAccessibleViewService))),
    ( (__param(4, IInstantiationService))),
    ( (__param(5, ITerminalService))),
    ( (__param(6, IConfigurationService))),
    ( (__param(7, IContextKeyService))),
    ( (__param(8, IAccessibilitySignalService)))
], TerminalAccessibleViewContribution)));
registerTerminalContribution(TerminalAccessibleViewContribution.ID, TerminalAccessibleViewContribution);
class TerminalAccessibilityHelpContribution extends Disposable {
    constructor() {
        super();
        this._register(AccessibilityHelpAction.addImplementation(105, 'terminal', async (accessor) => {
            const instantiationService = accessor.get(IInstantiationService);
            const terminalService = accessor.get(ITerminalService);
            const accessibleViewService = accessor.get(IAccessibleViewService);
            const instance = await terminalService.getActiveOrCreateInstance();
            await terminalService.revealActiveTerminal();
            const terminal = instance?.xterm;
            if (!terminal) {
                return;
            }
            accessibleViewService.show(instantiationService.createInstance(TerminalAccessibilityHelpProvider, instance, terminal));
        }, ( (ContextKeyExpr.or(
            TerminalContextKeys.focus,
             (ContextKeyExpr.and(accessibleViewIsShown,  (ContextKeyExpr.equals(
                accessibleViewCurrentProviderId.key,
                "terminal"
            ))))
        )))));
    }
}
registerTerminalContribution(TerminalAccessibilityHelpContribution.ID, TerminalAccessibilityHelpContribution);
class FocusAccessibleBufferAction extends Action2 {
    constructor() {
        super({
            id: "workbench.action.terminal.focusAccessibleBuffer" ,
            title: ( localize2WithPath(_moduleId, 0, "Focus Accessible Terminal View")),
            precondition: ( (ContextKeyExpr.or(
                TerminalContextKeys.processSupported,
                TerminalContextKeys.terminalHasBeenCreated
            ))),
            keybinding: [
                {
                    primary: 512  | 60 ,
                    secondary: [2048  | 16 ],
                    linux: {
                        primary: 512  | 60  | 1024 ,
                        secondary: [2048  | 16 ]
                    },
                    weight: 200 ,
                    when: ( (ContextKeyExpr.and(CONTEXT_ACCESSIBILITY_MODE_ENABLED, TerminalContextKeys.focus)))
                }
            ]
        });
    }
    async run(accessor, ...args) {
        const terminalService = accessor.get(ITerminalService);
        const terminal = await terminalService.getActiveOrCreateInstance();
        if (!terminal?.xterm) {
            return;
        }
        TerminalAccessibleViewContribution.get(terminal)?.show();
    }
}
registerAction2(FocusAccessibleBufferAction);
registerTerminalAction({
    id: "workbench.action.terminal.accessibleBufferGoToNextCommand" ,
    title: ( localize2WithPath(_moduleId, 1, "Accessible Buffer Go to Next Command")),
    precondition: ( (ContextKeyExpr.or(
        TerminalContextKeys.processSupported,
        TerminalContextKeys.terminalHasBeenCreated,
         (ContextKeyExpr.and(accessibleViewIsShown,  (ContextKeyExpr.equals(
            accessibleViewCurrentProviderId.key,
            "terminal"
        ))))
    ))),
    keybinding: [
        {
            primary: 512  | 18 ,
            when: ( (ContextKeyExpr.and(
                 (ContextKeyExpr.and(accessibleViewIsShown,  (ContextKeyExpr.equals(
                    accessibleViewCurrentProviderId.key,
                    "terminal"
                ))))
            ))),
            weight: 200  + 2
        }
    ],
    run: async (c) => {
        const instance = await c.service.activeInstance;
        if (!instance) {
            return;
        }
        await TerminalAccessibleViewContribution.get(instance)?.navigateToCommand("next" );
    }
});
registerTerminalAction({
    id: "workbench.action.terminal.accessibleBufferGoToPreviousCommand" ,
    title: ( localize2WithPath(_moduleId, 2, "Accessible Buffer Go to Previous Command")),
    precondition: ( (ContextKeyExpr.and( (ContextKeyExpr.or(
        TerminalContextKeys.processSupported,
        TerminalContextKeys.terminalHasBeenCreated
    )),  (ContextKeyExpr.and(accessibleViewIsShown,  (ContextKeyExpr.equals(
        accessibleViewCurrentProviderId.key,
        "terminal"
    ))))))),
    keybinding: [
        {
            primary: 512  | 16 ,
            when: ( (ContextKeyExpr.and(
                 (ContextKeyExpr.and(accessibleViewIsShown,  (ContextKeyExpr.equals(
                    accessibleViewCurrentProviderId.key,
                    "terminal"
                ))))
            ))),
            weight: 200  + 2
        }
    ],
    run: async (c) => {
        const instance = await c.service.activeInstance;
        if (!instance) {
            return;
        }
        await TerminalAccessibleViewContribution.get(instance)?.navigateToCommand("previous" );
    }
});
registerTerminalAction({
    id: "workbench.action.terminal.scrollToBottomAccessibleView" ,
    title: ( localize2WithPath(_moduleId, 3, 'Scroll to Accessible View Bottom')),
    precondition: ( (ContextKeyExpr.and( (ContextKeyExpr.or(
        TerminalContextKeys.processSupported,
        TerminalContextKeys.terminalHasBeenCreated
    )),  (ContextKeyExpr.and(accessibleViewIsShown,  (ContextKeyExpr.equals(
        accessibleViewCurrentProviderId.key,
        "terminal"
    ))))))),
    keybinding: {
        primary: 2048  | 13 ,
        linux: { primary: 1024  | 13  },
        when: ( (accessibleViewCurrentProviderId.isEqualTo("terminal" ))),
        weight: 200
    },
    run: (c, accessor) => {
        const accessibleViewService = accessor.get(IAccessibleViewService);
        const lastPosition = accessibleViewService.getLastPosition();
        if (!lastPosition) {
            return;
        }
        accessibleViewService.setPosition(lastPosition, true);
    }
});
registerTerminalAction({
    id: "workbench.action.terminal.scrollToTopAccessibleView" ,
    title: ( localize2WithPath(_moduleId, 4, 'Scroll to Accessible View Top')),
    precondition: ( (ContextKeyExpr.and( (ContextKeyExpr.or(
        TerminalContextKeys.processSupported,
        TerminalContextKeys.terminalHasBeenCreated
    )),  (ContextKeyExpr.and(accessibleViewIsShown,  (ContextKeyExpr.equals(
        accessibleViewCurrentProviderId.key,
        "terminal"
    ))))))),
    keybinding: {
        primary: 2048  | 14 ,
        linux: { primary: 1024  | 14  },
        when: ( (accessibleViewCurrentProviderId.isEqualTo("terminal" ))),
        weight: 200
    },
    run: (c, accessor) => {
        const accessibleViewService = accessor.get(IAccessibleViewService);
        accessibleViewService.setPosition({ lineNumber: 1, column: 1 }, true);
    }
});

export { TerminalAccessibilityHelpContribution, TerminalAccessibleViewContribution };
